import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { IPsyBlock, NewQuestionsPsy } from '../../components/tools/new-questions/new-question-psy';
import { ToolsPanel } from '../../components/meet/tools-panel';
import { DpdgPanelPsy } from '../../components/tools/dpdg/dpdg-panel-psy';
import { DetectorEmotionPsy } from '../../components/tools/detector-emo/detector-emo-psy';
import { MacCardsPsy } from '../../components/tools/mac-cards/mac-card-psy';

import { LifeBalanceWheelPsy } from '../../components/tools/life-balance-wheel/wheel-psy';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setActiveTool } from '../../store/meetActiveTool.slice';
import { setDpDgStop } from '../../store/dpdg.slice';
import { setMacDec, setMacSelected } from '../../store/mac.slice';
import { selectUser } from '../../store/auth.slice';

import { useDpdgState, Trajectory, normalizeTrajectory } from '../../hooks/useDpdgState';
import { useSocketDpdg } from '../../hooks/useSocketDpdg';
import { DpDgVideoPanelPsy } from '../../components/tools/dpdg/dpdg-video-psy';
import { clampBallSize } from '../../components/tools/dpdg/constants';
import { ColorCodePsy } from '../../components/tools/color-code/color-code-psy';
import {
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_GROW_AI,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../constants/featureFlags';
import { useFeatureFlagEnabled } from '../../hooks/useFeatureFlagEnabled';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';
import { CloudsPsy } from '../../components/tools/clouds/clouds-psy';
import { Line } from '../../components/tools/clouds/types';
import { GrowPsy } from '../../components/tools/grow/grow-psy';
import { SandTherapyPsy } from '../../components/tools/sand-therapy/sand-therapy-psy';
import { PlacedFigure } from '../../components/tools/sand-therapy/types';

export interface IMeetToolsPsyProps extends Omit<IPsyBlock, 'onSetMenu'> {
  /**
   * Мобильный 1:1: иконки инструментов поверх видео без фона, панели «Подкинь вопрос» и ДПДГ
   * открываются нижней шторкой, поле ДПДГ — на весь экран.
   */
  mobileLayout?: boolean;
  roomName?: string;
  consultationId?: number;
  users: any[];
  sendCloudsOpen?: () => void;
  sendCloudsClose?: () => void;
  clientCloudsLines?: Line[];
  clientCloudsSelectedImage?: number | null;
  clientCloudsFinished?: boolean;
  onFinishCloudsTool?: () => void;
  onColorCodeActiveTabChange?: (hasActiveTab: boolean) => void;
  onSaveClouds?: () => Promise<void>;
  sendSandTherapyOpen?: () => void;
  sendSandTherapyClose?: () => void;
  sandPlacedFigures?: PlacedFigure[];
  sandColor?: string;
  sandClientReady?: boolean;
  /** Внешнее управление выбранным инструментом (для открытия МАК из панели настроек) */
  menu?: PsyToolsId | 0;
  setMenu?: (m: PsyToolsId | 0) => void;
  /** Отправка колоды МАК всем клиентам через data channel */
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  sendMacCardsClose?: () => void;
  sendEmotionsOpen?: () => void;
  sendEmotionsClose?: () => void;
  emotionsClientInbound?: { nonce: number; result: { category: string[]; emo: string[] } } | null;
  onEmotionsClientInboundConsumed?: () => void;
  sendColorCodeOpen?: () => void;
  sendColorCodeClose?: () => void;
  colorCodeClientInbound?: { nonce: number; selectedColors: string[] } | null;
  onColorCodeClientInboundConsumed?: () => void;
  sendWheelOpen?: (
    payload:
      | {
          values: number[];
          sectorNames?: string[];
          isRealtimeVisible?: boolean;
          isSelectionUnlocked?: boolean;
        }
      | number[],
  ) => void;
  sendWheelClose?: () => void;
  wheelLiveKitEvent?: { nonce: number; kind: 'change' | 'result'; values: number[] } | null;
  onWheelLiveKitEventConsumed?: () => void;
  sendDpdgOpen?: (payload?: { initialSync?: Record<string, unknown> }) => void;
  sendDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
  sendDpdgClose?: () => void;
  sendDpdgVideoControl?: (payload: { type: string; time?: number }) => void;
  psychDpdgInbound?: { nonce: number; action: string; data: Record<string, unknown> } | null;
  onPsychDpdgInboundConsumed?: () => void;
  useDataChannelToolTransport?: boolean;
}

export const MeetToolsPsy: FC<IMeetToolsPsyProps> = ({
  mobileLayout = false,
  result,
  setResult,
  roomName,
  consultationId,
  users,
  onToolActivated,
  sendCloudsOpen,
  sendCloudsClose,
  clientCloudsLines,
  clientCloudsSelectedImage,
  clientCloudsFinished,
  onFinishCloudsTool,
  onColorCodeActiveTabChange,
  onSaveClouds,
  sendSandTherapyOpen,
  sendSandTherapyClose,
  sandPlacedFigures = [],
  sandColor = '#fef3c7',
  sandClientReady = false,
  menu: menuProp,
  setMenu: setMenuProp,
  sendMacCardsOpen,
  sendMacCardsClose,
  sendEmotionsOpen,
  sendEmotionsClose,
  emotionsClientInbound,
  onEmotionsClientInboundConsumed,
  sendColorCodeOpen,
  sendColorCodeClose,
  colorCodeClientInbound,
  onColorCodeClientInboundConsumed,
  sendWheelOpen,
  sendWheelClose,
  wheelLiveKitEvent,
  onWheelLiveKitEventConsumed,
  sendDpdgOpen,
  sendDpdgMessage,
  sendDpdgClose,
  sendDpdgVideoControl,
  psychDpdgInbound,
  onPsychDpdgInboundConsumed,
  useDataChannelToolTransport,
}) => {
  const settings = useAppSelector((state) => state.appSettingsSlice.settings);
  const user = useAppSelector(selectUser);
  const [internalMenu, setInternalMenu] = useState<PsyToolsId | 0>(0);
  const menu = menuProp !== undefined ? menuProp : internalMenu;
  const setMenu = setMenuProp ?? setInternalMenu;
  const [isGrowOpen, setIsGrowOpen] = useState(false);
  const [closeHidden, setCloseHidden] = useState(false);
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const hasColorCodeTool = Boolean(user?.active_psy_tools?.includes('color_code'));
  const canUseColorCode = isColorCodeEnabled && hasColorCodeTool;
  const hasCloudsTool = Boolean(user?.active_psy_tools?.includes('clouds'));
  const canUseClouds = isCloudsEnabled && hasCloudsTool;
  const hasSandTherapyTool = Boolean(user?.active_psy_tools?.includes('sand_therapy'));
  const hasSandTherapyEntitlement = useTariffToolCheck('sand_therapy');
  const canUseSandTherapy = isSandTherapyEnabled && hasSandTherapyEntitlement && hasSandTherapyTool;
  const isGrowAiEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_GROW_AI);
  const hasGrowTool = Boolean(user?.active_psy_tools?.includes('grow_ai'));
  const hasGrowEntitlement = useTariffToolCheck('grow_ai');
  const canUseGrow = isGrowAiEnabled && hasGrowEntitlement && hasGrowTool;

  const dispatch = useAppDispatch();

  const mapSliderToVelocity = (val: number): number => {
    const minV = 0.05;
    const maxV = 4;

    return ((val - 1) / 39) * (maxV - minV) + minV;
  };

  const {
    trajectory,
    setTrajectory,
    bgColor,
    setBgColor,
    ballColor,
    setBallColor,
    velocity,
    setVelocity,
    backgroundType,
    setBackgroundType,
    backgroundValue,
    setBackgroundValue,
    ballSize,
    setBallSize,
  } = useDpdgState({
    initialSpeed: settings?.defaults_dpdg_speed ? mapSliderToVelocity(Number(settings.defaults_dpdg_speed)) : 0.2,
    initialSize: settings?.defaults_dpdg_size ? Number(settings.defaults_dpdg_size) : 30,
    initialBackground: settings?.defaults_dpdg_background || '',
    initialTrajectory: 'horizontal',
  });

  const { emitChange } = useSocketDpdg(
    true,
    roomName || '',
    (data) => {
      if (data.trajectory) setTrajectory(data.trajectory);
      if (data.bgColor) setBgColor(data.bgColor);
      if (data.ballColor) setBallColor(data.ballColor);
      if (data.velocity !== undefined && data.velocity !== null) setVelocity(data.velocity);
      if (data.backgroundType !== undefined && data.backgroundType !== null) {
        setBackgroundType(data.backgroundType);
        setBackgroundValue(data.backgroundValue ?? '');
      }
      if (data.ballSize !== undefined) setBallSize(data.ballSize);
    },
    {
      socketDisabled: true,
      publishAction: sendDpdgMessage ? (action, data) => sendDpdgMessage(action, data) : undefined,
    },
  );

  const safeTrajectory = normalizeTrajectory(trajectory);

  const onSetMenu = useCallback(
    (newMenu: PsyToolsId | 0) => {
      if (newMenu === PsyToolsId.GROW) {
        if (!canUseGrow) {
          return;
        }
        setIsGrowOpen((prev) => !prev);
        return;
      }

      if (newMenu === PsyToolsId.COLOR_CODE && !canUseColorCode) {
        return;
      }
      if (newMenu === PsyToolsId.CLOUDS && !canUseClouds) {
        return;
      }
      if (newMenu === PsyToolsId.SAND_THERAPY && !canUseSandTherapy) {
        return;
      }
      switch (menu) {
        case PsyToolsId.WHEEL:
          sendWheelClose?.();
          break;
        case PsyToolsId.DPDG:
          sendDpdgClose?.();
          dispatch(setDpDgStop());
          setTrajectory('horizontal');
          break;
        case PsyToolsId.EMOTIONS:
          sendEmotionsClose?.();
          break;
        case PsyToolsId.MAC_CARDS:
          sendMacCardsClose?.();
          dispatch(setDpDgStop());
          dispatch(setMacSelected([]));
          dispatch(setDpDgStop());
          dispatch(setMacDec([]));
          break;
        case PsyToolsId.COLOR_CODE:
          sendColorCodeClose?.();
          break;
        case PsyToolsId.CLOUDS:
          sendCloudsClose?.();
          break;
        case PsyToolsId.SAND_THERAPY:
          sendSandTherapyClose?.();
          break;
      }

      dispatch(setActiveTool(0));
      setMenu(newMenu);
      setCloseHidden(false);
    },
    [
      menu,
      dispatch,
      setTrajectory,
      canUseColorCode,
      canUseClouds,
      canUseSandTherapy,
      canUseGrow,
      sendEmotionsClose,
      sendMacCardsClose,
      sendWheelClose,
      sendDpdgClose,
      sendColorCodeClose,
      sendCloudsClose,
      sendSandTherapyClose,
    ],
  );

  useEffect(() => {
    if (users.length === 1) {
      onSetMenu(0);
    }
  }, [onSetMenu, users.length]);

  useEffect(() => {
    if (!canUseColorCode && menu === PsyToolsId.COLOR_CODE) {
      setMenu(0);
    }
    if (!canUseClouds && menu === PsyToolsId.CLOUDS) {
      setMenu(0);
    }
    if (!canUseSandTherapy && menu === PsyToolsId.SAND_THERAPY) {
      setMenu(0);
    }
    if (!canUseGrow && isGrowOpen) {
      setIsGrowOpen(false);
    }
  }, [canUseColorCode, canUseClouds, canUseGrow, canUseSandTherapy, isGrowOpen, menu, setMenu]);

  const changeTrajectory = useCallback(
    (newTraj: Trajectory) => {
      setTrajectory(newTraj);
      emitChange('change_trajectory', { trajectory: newTraj });
    },
    [setTrajectory, emitChange],
  );

  const changeBgColor = useCallback(
    (color: string) => {
      setBgColor(color);
      emitChange('change_bg_color', { bgColor: color });
    },
    [setBgColor, emitChange],
  );

  const changeBallColor = useCallback(
    (color: string) => {
      setBallColor(color);
      emitChange('change_ball_color', { ballColor: color });
    },
    [setBallColor, emitChange],
  );

  const setBallVelocity = useCallback(
    (newVelocity: number) => {
      setVelocity(newVelocity);
      emitChange('change_velocity', { velocity: newVelocity });
    },
    [setVelocity, emitChange],
  );

  const changeBallSize = useCallback(
    (size: number) => {
      const clampedSize = clampBallSize(size);
      setBallSize(clampedSize);
      emitChange('change_ball_size', { ballSize: clampedSize });
    },
    [setBallSize, emitChange],
  );

  const activePanel = useMemo(() => {
    switch (menu) {
      case PsyToolsId.EMOTIONS:
        return (
          <DetectorEmotionPsy
            mobile={mobileLayout}
            result={result}
            setResult={setResult}
            onToolActivated={onToolActivated}
            sendEmotionsOpen={sendEmotionsOpen}
            emotionsClientInbound={emotionsClientInbound}
            onEmotionsClientInboundConsumed={onEmotionsClientInboundConsumed}
          />
        );

      case PsyToolsId.MAC_CARDS:
        return (
          <MacCardsPsy
            mobile={mobileLayout}
            setResult={setResult}
            onCardZoom={setCloseHidden}
            onToolActivated={onToolActivated}
            sendMacCardsOpen={sendMacCardsOpen}
          />
        );

      case PsyToolsId.QUESTION:
        return (
          <NewQuestionsPsy
            result={result}
            setResult={setResult}
            onToolActivated={onToolActivated}
            mobile={mobileLayout}
          />
        );

      case PsyToolsId.DPDG:
        return (
          <DpdgPanelPsy
            mobile={mobileLayout}
            changeTrajectory={changeTrajectory}
            result={result}
            setResult={setResult}
            bgColor={bgColor}
            changeBgColor={changeBgColor}
            changeBallColor={changeBallColor}
            ballColor={ballColor}
            velocity={velocity}
            setBallVelocity={setBallVelocity}
            backgroundType={backgroundType}
            setBackgroundType={setBackgroundType}
            backgroundValue={backgroundValue}
            setBackgroundValue={setBackgroundValue}
            setBallSize={changeBallSize}
            ballSize={ballSize}
            sendDpdgOpen={sendDpdgOpen}
            sendDpdgMessage={sendDpdgMessage}
            sendDpdgClose={sendDpdgClose}
          />
        );

      case PsyToolsId.WHEEL:
        return (
          <LifeBalanceWheelPsy
            mobile={mobileLayout}
            result={result}
            setResult={setResult}
            onToolActivated={onToolActivated}
            sendWheelOpen={sendWheelOpen}
            wheelLiveKitEvent={wheelLiveKitEvent}
            onWheelLiveKitEventConsumed={onWheelLiveKitEventConsumed}
            onSettingsOpenChange={setCloseHidden}
          />
        );

      case PsyToolsId.COLOR_CODE:
        return canUseColorCode ? (
          <ColorCodePsy
            mobile={mobileLayout}
            result={result}
            setResult={setResult}
            onToolActivated={onToolActivated}
            onActiveTabChange={onColorCodeActiveTabChange}
            sendColorCodeOpen={sendColorCodeOpen}
            clientColorInbound={colorCodeClientInbound}
            onClientColorInboundConsumed={onColorCodeClientInboundConsumed}
          />
        ) : null;

      case PsyToolsId.CLOUDS:
        return canUseClouds ? (
          <CloudsPsy
            mobile={mobileLayout}
            roomName={roomName}
            consultationId={consultationId}
            result={result}
            setResult={setResult}
            onToolActivated={onToolActivated}
            sendCloudsOpen={sendCloudsOpen}
            sendCloudsClose={sendCloudsClose}
            clientLines={clientCloudsLines}
            clientSelectedImage={clientCloudsSelectedImage}
            clientFinished={clientCloudsFinished}
            onFinishTool={onFinishCloudsTool}
            onCloseTool={() => onSetMenu(0)}
            onSaveClouds={onSaveClouds}
          />
        ) : null;

      case PsyToolsId.SAND_THERAPY:
        return canUseSandTherapy ? (
          <SandTherapyPsy
            mobile={mobileLayout}
            roomName={roomName}
            sendSandTherapyOpen={sendSandTherapyOpen}
            sendSandTherapyClose={sendSandTherapyClose}
            placedFigures={sandPlacedFigures}
            sandColor={sandColor}
            clientReady={sandClientReady}
            onCloseTool={() => onSetMenu(0)}
            result={result}
            setResult={setResult}
          />
        ) : null;

      case PsyToolsId.GROW:
        return null;

      default:
        return null;
    }
  }, [
    menu,
    mobileLayout,
    result,
    setResult,
    changeTrajectory,
    roomName,
    bgColor,
    changeBgColor,
    changeBallColor,
    ballColor,
    velocity,
    setBallVelocity,
    backgroundType,
    backgroundValue,
    changeBallSize,
    ballSize,
    setCloseHidden,
    onToolActivated,
    canUseColorCode,
    canUseClouds,
    onColorCodeActiveTabChange,
    sendCloudsOpen,
    sendCloudsClose,
    clientCloudsLines,
    clientCloudsSelectedImage,
    clientCloudsFinished,
    onFinishCloudsTool,
    onSetMenu,
    onSaveClouds,
    canUseSandTherapy,
    sendSandTherapyOpen,
    sendSandTherapyClose,
    sendSandTherapyClose,
    sendEmotionsOpen,
    emotionsClientInbound,
    onEmotionsClientInboundConsumed,
    sendColorCodeOpen,
    sendColorCodeClose,
    colorCodeClientInbound,
    onColorCodeClientInboundConsumed,
    sendWheelOpen,
    sendWheelClose,
    wheelLiveKitEvent,
    onWheelLiveKitEventConsumed,
    sendDpdgOpen,
    sendDpdgMessage,
    sendDpdgClose,
    sandPlacedFigures,
    sandColor,
    sandClientReady,
  ]);

  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);

  return (
    <div className="h-full min-h-0 w-[100%] flex flex-row gap-4">
      <ToolsPanel
        menu={menu || undefined}
        setMenu={onSetMenu}
        isGrowOpen={isGrowOpen}
        closeHidden={closeHidden}
        transparentBackground={mobileLayout}
        mobileSheet={mobileLayout}
        users={users}
        roomName={roomName}
        panel={
          activePanel ? (
            <div
              className={
                mobileLayout
                  ? 'h-full w-full' // без overflow-hidden: иначе не работает sticky-панель кнопок в шторке
                  : 'box-border h-full min-h-0 w-full min-w-0 max-w-full gap-0 overflow-hidden px-[0_0.5rem] py-[0.5rem]'
              }
            >
              {activePanel}
            </div>
          ) : null
        }
      />
      {isGrowOpen && canUseGrow && (
        <GrowPsy
          result={result}
          setResult={setResult}
          onToolActivated={onToolActivated}
          onClosePanel={() => setIsGrowOpen(false)}
        />
      )}
      {activeTool === PsyToolsId.DPDG && (
        <DpDgVideoPanelPsy
          mobile={mobileLayout}
          bgColor={bgColor}
          ballColor={ballColor}
          trajectory={safeTrajectory}
          changeTrajectory={changeTrajectory}
          roomName={roomName}
          velocity={velocity}
          backgroundType={backgroundType}
          backgroundValue={backgroundValue}
          ballSize={ballSize}
          sendDpdgVideoControl={sendDpdgVideoControl}
          sendDpdgMessage={sendDpdgMessage}
          useDataChannelToolTransport
        />
      )}
      {menu === PsyToolsId.GROW && canUseGrow && (
        <GrowPsy result={result} setResult={setResult} onToolActivated={onToolActivated} />
      )}
    </div>
  );
};
